import { getBcp47, type TSupportedLocale } from "./locales";

/**
 * Named date display presets. Each maps to `Intl.DateTimeFormatOptions`
 * so the output automatically respects locale conventions (day/month
 * order, separators, month names, etc.).
 *
 * | Preset              | en-GB example          | de-DE example            |
 * |---------------------|------------------------|--------------------------|
 * | `full`              | Monday, 29 June 2026   | Montag, 29. Juni 2026    |
 * | `long`              | 29 June 2026           | 29. Juni 2026            |
 * | `medium`            | 29 Jun 2026            | 29. Juni 2026            |
 * | `short`             | 29/06/2026             | 29.06.2026               |
 * | `dayMonth`          | 29 Jun                 | 29. Juni                 |
 * | `dayMonthLong`      | 29 June                | 29. Juni                 |
 * | `weekdayDayMonth`   | Mon, 29 Jun 2026       | Mo., 29. Juni 2026       |
 * | `weekdayFull`       | Monday                 | Montag                   |
 * | `monthYear`         | June 2026              | Juni 2026                |
 * | `weekdayDayMonthFull` | Monday, 29 June      | Montag, 29. Juni         |
 * | `weekdayShortDayMonth`| Wed, 29 Jun          | Mi., 29. Juni            |
 */
export type TDatePreset =
  | "full"
  | "long"
  | "medium"
  | "short"
  | "dayMonth"
  | "dayMonthLong"
  | "weekdayDayMonth"
  | "weekdayFull"
  | "weekdayShort"
  | "monthYear"
  | "weekdayDayMonthFull"
  | "weekdayShortDayMonth";

const DATE_PRESET_OPTIONS: Record<TDatePreset, Intl.DateTimeFormatOptions> = {
  full: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
  long: { day: "numeric", month: "long", year: "numeric" },
  medium: { day: "numeric", month: "short", year: "numeric" },
  short: { day: "2-digit", month: "2-digit", year: "numeric" },
  dayMonth: { day: "numeric", month: "short" },
  dayMonthLong: { day: "numeric", month: "long" },
  weekdayDayMonth: {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  },
  weekdayFull: { weekday: "long" },
  weekdayShort: { weekday: "short" },
  monthYear: { month: "long", year: "numeric" },
  weekdayDayMonthFull: { weekday: "long", day: "numeric", month: "long" },
  weekdayShortDayMonth: { weekday: "short", day: "numeric", month: "short" },
};

/**
 * Formats a date using a named preset and the given locale.
 * Uses `Intl.DateTimeFormat` so output automatically follows locale conventions
 * (day/month order, separators, translated month and weekday names).
 *
 * @example
 * formatDate(new Date("2026-06-29"), "full", "en")
 * // "Monday, 29 June 2026"
 *
 * formatDate(new Date("2026-06-29"), "full", "de")
 * // "Montag, 29. Juni 2026"
 */
export const formatDate = (
  date: Date | number | string,
  preset: TDatePreset,
  locale: TSupportedLocale,
): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const bcp47 = getBcp47(locale);
  return new Intl.DateTimeFormat(bcp47, DATE_PRESET_OPTIONS[preset]).format(d);
};

const DATE_FALLBACK = "--";

/**
 * Like `formatDate` but handles `null`, `undefined`, and invalid dates
 * by returning a fallback string.
 */
export const formatDateWithFallback = (
  date: Date | string | null | undefined,
  preset: TDatePreset,
  locale: TSupportedLocale,
  fallback: string = DATE_FALLBACK,
): string => {
  if (date == null) {
    return fallback;
  }
  const d = typeof date === "string" ? new Date(date) : date;
  if (d instanceof Date && Number.isNaN(d.getTime())) {
    return fallback;
  }
  return formatDate(d, preset, locale);
};
